// Calendar + Task lifecycle API client + pure helpers (Track 2 web port).
// Backend contract: TRACK2-BACKEND-REPORT.md
//   GET  /calendar?from&to&scope        — aggregate (task|meeting|followup)
//   POST /tasks/:id/complete            — idempotent
//   POST /tasks/:id/postpone {scheduledDate}
//   POST /tasks {title,assignedToId?,dueDate?,scheduledDate?,movable?}
import { api } from '@/utils/api';

export type CalendarItemType = 'task' | 'meeting' | 'followup';
export type CalendarScope = 'own' | 'team' | 'all';

export interface CalendarItem {
  type: CalendarItemType;
  id: string;
  date: string; // ISO — scheduledDate ?? dueDate for tasks
  title: string;
  // task-only actionable fields:
  status?: string;
  movable?: boolean;
  dueDate?: string | null;
  scheduledDate?: string | null;
  postponedCount?: number;
  assignedToId?: string;
  overdue?: boolean;
  // meeting/followup deep-link:
  linkId?: string;
}

export interface CalendarResponse {
  success: boolean;
  scope: CalendarScope;
  from: string;
  to: string;
  data: CalendarItem[];
}

export interface TaskRecord {
  id: string;
  title: string;
  status: string;
  movable: boolean;
  dueDate?: string | null;
  scheduledDate?: string | null;
  postponedCount?: number;
  assignedToId?: string | null;
  createdById?: string | null;
}

// ── pure helpers (unit-tested) ──────────────────────────────────────────────

const MANAGER_ROLES = ['owner', 'admin', 'manager'];

/** Roles that may lock a task (movable=false) or assign it to someone else. */
export function canManageTasks(roleName?: string | null): boolean {
  return MANAGER_ROLES.includes((roleName ?? '').toLowerCase());
}

/**
 * Can the current user postpone this item?
 * - Only `type:'task'` items are postponeable at all.
 * - A locked task (movable===false) can only be moved by a manager/admin/owner.
 *   (The backend enforces this too; this mirrors it for UX so we disable the button.)
 */
export function canPostpone(item: CalendarItem, roleName?: string | null): boolean {
  if (item.type !== 'task') return false;
  if (item.movable === false && !canManageTasks(roleName)) return false;
  return true;
}

/** Month window [from,to] as ISO date strings (yyyy-mm-dd) for a given Date in that month. */
export function monthWindow(d: Date): { from: string; to: string } {
  const y = d.getFullYear();
  const m = d.getMonth();
  const first = new Date(y, m, 1);
  const last = new Date(y, m + 1, 0);
  const iso = (x: Date) =>
    `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  return { from: iso(first), to: iso(last) };
}

/** FullCalendar background/border colour per item type (+ red when overdue). */
export function eventColor(item: CalendarItem): { color: string; textColor: string } {
  if (item.overdue) return { color: '#EF4444', textColor: '#ffffff' };
  switch (item.type) {
    case 'task':
      return { color: '#2FA037', textColor: '#ffffff' };
    case 'meeting':
      return { color: '#2A55B5', textColor: '#ffffff' };
    case 'followup':
      return { color: '#F59E0B', textColor: '#1C140F' };
    default:
      return { color: '#6B6B60', textColor: '#ffffff' };
  }
}

/** Map a calendar item to a FullCalendar EventInput. */
export function toFullCalendarEvent(item: CalendarItem) {
  const { color, textColor } = eventColor(item);
  return {
    id: `${item.type}:${item.id}`,
    title: item.title,
    start: item.date,
    allDay: true,
    color,
    textColor,
    extendedProps: { item },
  };
}

// ── network calls ───────────────────────────────────────────────────────────

export async function fetchCalendar(params: {
  from: string;
  to: string;
  scope?: CalendarScope;
}): Promise<CalendarResponse> {
  const res = await api.get('/calendar', { params });
  return res.data as CalendarResponse;
}

export async function completeTask(id: string): Promise<TaskRecord> {
  const res = await api.post(`/tasks/${id}/complete`);
  return (res.data?.data ?? res.data) as TaskRecord;
}

export async function postponeTask(id: string, scheduledDate: string): Promise<TaskRecord> {
  const res = await api.post(`/tasks/${id}/postpone`, { scheduledDate });
  return (res.data?.data ?? res.data) as TaskRecord;
}

export interface CreateTaskInput {
  title: string;
  description?: string;
  priority?: string;
  assignedToId?: string;
  dueDate?: string; // hard deadline
  scheduledDate?: string; // do-it-on day
  movable?: boolean;
}

export async function createTask(input: CreateTaskInput): Promise<TaskRecord> {
  const res = await api.post('/tasks', input);
  return (res.data?.data ?? res.data) as TaskRecord;
}
